import { useCallback, useEffect, useMemo, useState } from 'react'

import useFetch from '@e3dc-react/shell/Hooks/useFetch'
import { CompanyUserData } from 'src/State/User/UserModel'
import { useTypedSelector } from 'src/State/RootReducer'
import {
  useCompanyErrorNotification,
  companyUserToCompanySettingsUser,
  CompanyErrorNotificationModel,
  CompanySettingsUserDataModel,
} from 'src/Libs/company-settings'

interface UseCompanyUsersHook {
  /**
   *
   *
   * @param companyId
   */
  users: CompanySettingsUserDataModel[] | undefined
  loading: boolean
}

/**
 * Provides the linked company users, together with related notification information.
 *
 * The required composition of both data streams is blocking using a server-based pagination for these results.
 */
const useCompanyUsers = (companyId: number, userId?: number): UseCompanyUsersHook => {
  const refresh = useTypedSelector(state => state.apiView.refresh)
  const [users, setUsers] = useState<CompanySettingsUserDataModel[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const { loadAll } = useCompanyErrorNotification()

  const [installers, installersLoading] = useFetch<CompanyUserData[]>({
    url: `users/companies/${companyId}/users`,
    doNotFetch: !userId || !companyId,
    dependencies: [refresh],
  })

  const loadErrorNotifications = useCallback(async (companyId: number): Promise<CompanyErrorNotificationModel[]> => {
    setLoading(true)
    return loadAll(companyId)
      .then(result => result)
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshUsers = useCallback(async (installers: CompanyUserData[]): Promise<void> => {
    if (installers) {
      const usersWithNotifications = await loadErrorNotifications(companyId)
      const companySettingsUsers = companyUserToCompanySettingsUser(installers, usersWithNotifications)
      setUsers(companySettingsUsers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    refreshUsers(installers || []).catch(console.error)
  }, [installers, refreshUsers])

  const isLoading = useMemo<boolean>(() => {
    return loading || installersLoading
  }, [loading, installersLoading])

  return { users, loading: isLoading }
}

export default useCompanyUsers
